import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";

export default function Liberty() {
  const { t } = useLang();

  const KPIS = [
    { value: "11", label: t("lp_kpi_months") },
    { value: "1,24%", label: t("lp_kpi_avg") },
    { value: "14,6%", label: t("lp_kpi_total") },
    { value: "€ 1.000", label: t("lp_kpi_min") },
  ];

  const LEVELS = [
    { n: "01", name: t("lvl1_name"), alloc: t("lvl1_alloc"), text: t("lvl1_text") },
    { n: "02", name: t("lvl2_name"), alloc: t("lvl2_alloc"), text: t("lvl2_text") },
    { n: "03", name: t("lvl3_name"), alloc: t("lvl3_alloc"), text: t("lvl3_text") },
    { n: "04", name: t("lvl4_name"), alloc: t("lvl4_alloc"), text: t("lvl4_text") },
  ];

  const PILLARS = [
    { name: t("pil1_name"), text: t("pil1_text") },
    { name: t("pil2_name"), text: t("pil2_text") },
    { name: t("pil3_name"), text: t("pil3_text") },
  ];

  return (
    <div data-testid="liberty-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24">
        <MaskedLine delay={0.1}><span className="eyebrow">{t("lp_eyebrow")}</span></MaskedLine>
        <h1 className="mt-8 max-w-4xl text-4xl sm:text-5xl lg:text-6xl leading-[1.08]" data-testid="liberty-page-title">
          <MaskedLine delay={0.25}>
            <>{t("lp_hero_pre")} <em className="italic text-gold">{t("lp_hero_em")}</em>.</>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted leading-relaxed">{t("lp_hero_text")}</p>
        </Reveal>
      </section>

      {/* IL PROGRAMMA — KPI */}
      <section className="border-t border-pine/10" data-testid="liberty-program-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 min-w-0">
            <Reveal><Eyebrow>{t("lp_prog_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl leading-tight" data-testid="liberty-program-title">
                {t("lp_prog_pre")} <em className="italic text-gold">{t("lp_prog_em")}</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg text-ink-muted leading-relaxed">{t("lp_prog_text")}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 min-w-0">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-px bg-pine/10 border border-pine/10" data-testid="liberty-kpi-grid">
                {KPIS.map((k) => (
                  <div key={k.label} className="bg-cream p-8 transition-colors duration-300 hover:bg-surface" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="font-serif text-4xl sm:text-5xl text-pine">{k.value}</p>
                    <div className="mt-3 h-px w-8 bg-gold" aria-hidden="true" />
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-ink-muted">{k.label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-ink-muted leading-relaxed">{t("lp_prog_note")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ARCHITETTURA — 4 LIVELLI */}
      <section className="bg-surface border-y border-pine/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4 min-w-0">
            <Reveal><Eyebrow>{t("lp_arch_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl leading-tight" data-testid="liberty-architecture-title">
                {t("lp_arch_pre")} <em className="italic text-gold">{t("lp_arch_em")}</em>{t("lp_arch_post")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg text-ink-muted leading-relaxed">{t("lp_arch_text")}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 min-w-0">
            <ol className="relative border-s border-gold/50 ps-10 space-y-14">
              {LEVELS.map((l, i) => (
                <Reveal key={l.n} delay={i * 0.1}>
                  <li className="relative" data-testid={`liberty-level-${l.n}`}>
                    <span className="absolute -start-[3.28rem] top-1 flex h-5 w-5 rotate-45 items-center justify-center bg-surface border border-gold" aria-hidden="true" />
                    <p className="font-serif text-lg text-gold">{l.n}</p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <h3 className="font-serif text-3xl sm:text-4xl tracking-[0.04em] text-ink">{l.name}</h3>
                      <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">{l.alloc}</span>
                    </div>
                    <p className="mt-3 max-w-lg text-base text-ink-muted leading-relaxed">{l.text}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* VALORI */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
        <Reveal><Eyebrow>{t("lp_val_eyebrow")}</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-3xl sm:text-4xl leading-tight" data-testid="liberty-values-title">
            {t("lp_val_pre")} <em className="italic text-gold">{t("lp_val_em")}</em>.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-px bg-pine/10 border border-pine/10">
          {PILLARS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08} className="h-full">
              <div className="bg-cream h-full p-8 sm:p-10 transition-colors duration-300 hover:bg-surface" data-testid={`pillar-${i + 1}`}>
                <h3 className="font-serif text-2xl sm:text-3xl tracking-[0.1em] text-pine">{p.name}</h3>
                <div className="mt-4 h-px w-10 bg-gold" aria-hidden="true" />
                <p className="mt-5 text-base text-ink-muted leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-pine/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 flex flex-col items-start gap-8">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl leading-tight max-w-2xl" data-testid="liberty-closing-title">
              {t("lp_close_pre")} <em className="italic text-gold">{t("lp_close_em")}</em>{t("lp_close_post")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              href="https://libertyprivateoffice.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="liberty-page-external-cta"
            >
              libertyprivateoffice.com <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
